<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import PlayerDetailsSidebar from '../PlayerDetailsSidebar.vue'
import PlayerStatsSummary from '../PlayerStatsSummary.vue'
import PlayerGameStatsTable from '../PlayerGameStatsTable.vue'
import axios from '../../axios'

const props = defineProps({
  season: {
    type: String,
    required: true
  },
  playerId: {
    type: [String, Number],
    required: true
  },
  show: {
    type: Boolean,
    required: true
  },
  templateData: {
    type: Object,
    default: null
  },
  context: {
    type: String,
    default: 'splits',
    validator: (value) => ['splits', 'minutes'].includes(value)
  }
})

const emit = defineEmits(['close'])

// Simple reactive state
const loading = ref(false)
const rows = ref([])
const filteredGames = ref([])
const calculatedStats = ref(null)
const showSavePopup = ref(false)

// Settings state
const settings = ref({
  seasons: [],
  leagues: [],
  additionalFilter: 'allGames'
})

// Available data
const seasons = [
  '2025-2026', '2024-2025', '2023-2024', '2022-2023', '2021-2022', 
  '2020-2021', '2019-2020', '2018-2019', '2017-2018'
]

const availableLeagues = computed(() => [
  { id: 'friendly-international', name: 'Friendly International' },
  { id: 'adriatic-aba-liga-2', name: 'Adriatic ABA Liga 2' },
  { id: 'adriatic-liga-aba', name: 'Adriatic Liga ABA' },
  { id: 'adriatic-liga-aba-playoffs', name: 'Adriatic Liga ABA Playoffs' },
  { id: 'argentina-la-liga-torneo-super-20', name: 'Argentina La Liga Torneo Super 20' },
  { id: 'argentina-liga-a', name: 'Argentina Liga A' },
  { id: 'australia-nbl', name: 'Australia NBL' },
  { id: 'austria-superliga', name: 'Austria Superliga' },
  { id: 'basketball-champions-league', name: 'Basketball Champions League' },
  { id: 'basketball-champions-league-americas', name: 'Basketball Champions League Americas' },
  { id: 'bnxt-league', name: 'BNXT League' },
  { id: 'bosnia-division-i', name: 'Bosnia Division I' },
  { id: 'bulgaria-nbl', name: 'Bulgaria NBL' },
  { id: 'china-cba', name: 'China CBA' },
  { id: 'croatia-liga', name: 'Croatia Liga' },
  { id: 'czech-republic-mattoni-nbl', name: 'Czech Republic Mattoni NBL' },
  { id: 'denmark-ligaen', name: 'Denmark Ligaen' },
  { id: 'eurocup', name: 'EuroCup' },
  { id: 'euroleague', name: 'EuroLeague' },
  { id: 'fiba-europe-cup', name: 'FIBA Europe Cup' },
  { id: 'finland-korisliiga', name: 'Finland Korisliiga' },
  { id: 'france-betclic-elite', name: 'France Betclic Elite' },
  { id: 'france-betclic-elite-playoffs', name: 'France Betclic Elite Playoffs' },
  { id: 'france-elite-2', name: 'France Elite 2' },
  { id: 'georgia-superleague', name: 'Georgia Superleague' },
  { id: 'germany-easycredit-bbl', name: 'Germany EasyCredit BBL' },
  { id: 'germany-pro-a', name: 'Germany Pro A' },
  { id: 'greece-heba-a1', name: 'Greece HEBA A1' },
  { id: 'greece-heba-a2', name: 'Greece HEBA A2' },
  { id: 'hungary-a-league', name: 'Hungary A League' },
  { id: 'iceland-express-league', name: 'Iceland Express League' },
  { id: 'israel-winner-league', name: 'Israel Winner League' },
  { id: 'italy-lba-serie-a', name: 'Italy LBA Serie A' },
  { id: 'italy-serie-a2', name: 'Italy Serie A2' },
  { id: 'japan-b1-league', name: 'Japan B1 League' },
  { id: 'japan-b2-league', name: 'Japan B2 League' },
  { id: 'latvian-estonian-basketball-league', name: 'Latvian-Estonian Basketball League' },
  { id: 'lithuania-betsafe-lkl', name: 'Lithuania Betsafe LKL' },
  { id: 'lithuania-betsafe-lkl-playoffs', name: 'Lithuania Betsafe LKL Playoffs' },
  { id: 'lithuania-nkl', name: 'Lithuania NKL' },
  { id: 'mexico-liga-sisnova-lnbp', name: 'Mexico Liga Sisnova LNBP' },
  { id: 'nba', name: 'NBA' },
  { id: 'ncaa', name: 'NCAA' },
  { id: 'new-zealand-nbl', name: 'New Zealand NBL' },
  { id: 'norway-blno', name: 'Norway BLNO' },
  { id: 'poland-plk', name: 'Poland PLK' },
  { id: 'portugal-liga-profissional', name: 'Portugal Liga Profissional' },
  { id: 'puerto-rico-bsn', name: 'Puerto Rico BSN' },
  { id: 'romania-division-a', name: 'Romania Division A' },
  { id: 'serbia-kls', name: 'Serbia KLS' },
  { id: 'slovakia-extraliga', name: 'Slovakia Extraliga' },
  { id: 'slovenia-liga-nova-kbm', name: 'Slovenia Liga Nova KBM' },
  { id: 'spain-leb-gold', name: 'Spain LEB Gold' },
  { id: 'spain-liga-endesa', name: 'Spain Liga Endesa' },
  { id: 'super-league-basketball', name: 'Super League Basketball' },
  { id: 'sweden-basketligan', name: 'Sweden Basketligan' },
  { id: 'switzerland-sbl', name: 'Switzerland SBL' },
  { id: 'turkey-bsl', name: 'Turkey BSL' },
  { id: 'turkey-tbl', name: 'Turkey TBL' },
  { id: 'vtb-united-league', name: 'VTB United League' },
  { id: 'world-cup', name: 'World Cup' },
  { id: 'eurobasket', name: 'EuroBasket' },
  { id: 'eurobasket-2025', name: 'EuroBasket 2025' },
  { id: 'world-cup-european-qualifiers', name: 'World Cup European Qualifiers' },
  { id: 'eurobasket-qualifying-rounds', name: 'EuroBasket Qualifying Rounds' },
  { id: 'olympic-games', name: 'Olympic Games' },
  { id: 'olympic-games-qualifying-tournament', name: 'Olympic Games Qualifying Tournament' },
  { id: 'basketball-champions-league-qualifying-rounds', name: 'Basketball Champions League Qualifying Rounds' },
  { id: 'preparation', name: 'Preparation' },
  { id: 'enbl', name: 'ENBL' },
  { id: 'brazil-nbb', name: 'Brazil NBB' },
  { id: 'brazilian-nbb', name: 'Brazilian NBB' },
  { id: 'argentina-liga-a', name: 'Argentina Liga A' },
  { id: 'king-mindaugas-cup-play-offs', name: 'King Mindaugas Cup Play Off' },
  { id: 'latvian-cup', name: 'Latvian Cup' },
  { id: 'turkish-cup', name: 'Turkish Cup' },
  { id: 'italian-cup', name: 'Italian Cup' },
  { id: 'spanish-cup', name: 'Spanish Cup' },
])

const columns = [ 
  { key: 'game_date', label: 'Date' },
  { key: 'season', label: 'Season' },
  { key: 'league_name', label: 'League' },
  { key: 'team_name', label: 'Team' },
  { key: 'opponent_team_name', label: 'Opponent' },
  { key: 'minutes', label: 'Min' },
  { key: 'points', label: 'PTS' },
  { key: 'total_rebounds', label: 'REB' },
  { key: 'assists', label: 'AST' },
  { key: 'three_point_field_goal', label: '3PM' },
  { key: 'steals', label: 'STL' },
  { key: 'blocks', label: 'BLK' },
  { key: 'turnovers', label: 'TO' },
  { key: 'pra', label: 'PRA' },
  { key: 'efficiency', label: 'EFF' }
]

// Computed properties
const hasExistingSettings = computed(() => !!getExistingSettings())

// Simple functions
async function getExistingSettings() {
  // The modal is mounted while hidden with no player; don't call the API until there is one
  if (!props.playerId) return null

  try {
    // Fetch season template data from the database (using splits endpoint)
    const response = await axios.get(`/api/v2/projections-players/splits/${props.playerId}`)
    const seasonTemplates = response.data || []
    
    // Find the template that matches the current season type
    const template = seasonTemplates.find(t => t.template_label === props.season)
    
    if (template) {
      console.log('=== Template found in database ===')
      console.log('Raw template data:', template)
      console.log('Raw seasons:', template.seasons)
      console.log('Raw selected_leagues:', template.selected_leagues)
      console.log('Raw additional_filter:', template.additional_filter)
      
      // Parse the JSON strings from the database
      let seasons = []
      let leagues = []
      
      // Handle seasons parsing
      if (template.seasons) {
        if (typeof template.seasons === 'string') {
          try {
            seasons = JSON.parse(template.seasons)
          } catch (e) {
            console.error('Error parsing seasons:', e)
            seasons = []
          }
        } else if (Array.isArray(template.seasons)) {
          seasons = template.seasons
        }
      }
      
      // Handle leagues parsing
      if (template.selected_leagues) {
        if (typeof template.selected_leagues === 'string') {
          try {
            leagues = JSON.parse(template.selected_leagues)
          } catch (e) {
            console.error('Error parsing selected_leagues:', e)
            leagues = []
          }
        } else if (Array.isArray(template.selected_leagues)) {
          leagues = template.selected_leagues
        }
      }
      
      console.log('Parsed seasons:', seasons)
      console.log('Parsed leagues:', leagues)
      console.log('Leagues count:', leagues.length)
      
      // Create calculated stats object from the flat structure
      const calculatedStats = {
        gameCount: 10, // Default, could be stored separately
        stats: {
          points: { total: parseFloat(template.points || 0), per30: parseFloat(template.points || 0) },
          rebounds: { total: parseFloat(template.total_rebounds || 0), per30: parseFloat(template.total_rebounds || 0) },
          assists: { total: parseFloat(template.assists || 0), per30: parseFloat(template.assists || 0) },
          '3pt': { total: parseFloat(template.three_points_field_goal || 0), per30: parseFloat(template.three_points_field_goal || 0) },
          steals: { total: parseFloat(template.steals || 0), per30: parseFloat(template.steals || 0) },
          blocks: { total: parseFloat(template.blocks || 0), per30: parseFloat(template.blocks || 0) },
          turnovers: { total: parseFloat(template.turnovers || 0), per30: parseFloat(template.turnovers || 0) },
          pra: { total: 0, per30: 0 } // Calculate PRA from other stats
        }
      }
      
      return {
        seasons,
        leagues,
        additional_filter: template.additional_filter || 'allGames', // Read from database
        calculated_stats: calculatedStats,
        points: template.points,
        total_rebounds: template.total_rebounds,
        assists: template.assists,
        three_points_field_goal: template.three_points_field_goal,
        steals: template.steals,
        blocks: template.blocks,
        turnovers: template.turnovers
      }
    }
    
    return null
  } catch (error) {
    console.error('Error fetching season template data:', error)
    return null
  }
}

function getDefaultSeasons() {
  switch (props.season) {
    case 'Last Season':
      return ['2024-2025']
    case '2 Seasons ago':
      return ['2023-2024']
    case '3 Seasons ago':
      return ['2022-2023']
    case 'League Avg':
      return ['2024-2025', '2023-2024', '2022-2023']
    case 'Last 5 games':
      return ['2025-2026']
    case 'Rest of Curr Season':
    case 'Rest of Curr season':
      return ['2025-2026']
    default:
      return ['2025-2026']
  }
}

// Initialize settings
async function initializeSettings() {
  // First check if we have template data from the minutes settings table
  if (props.templateData) {
    settings.value.seasons = props.templateData.seasons || getDefaultSeasons()
    settings.value.leagues = props.templateData.leagues || []
    settings.value.additionalFilter = props.templateData.additionalFilter || 'allGames'
    console.log('Applied template data from minutes settings:', settings.value)
    console.log('Template data leagues:', props.templateData.leagues)
    console.log('Template data seasons:', props.templateData.seasons)
    console.log('Template data additional filter:', props.templateData.additionalFilter)
    
    // Fetch games with the template data
    await fetchGames(settings.value.leagues, settings.value.seasons, settings.value.additionalFilter)
    return
  }
  
  const existingSettings = await getExistingSettings()
  
  if (existingSettings) {
    // Use saved settings
    settings.value.seasons = existingSettings.seasons || getDefaultSeasons()
    settings.value.leagues = existingSettings.leagues || []
    settings.value.additionalFilter = existingSettings.additional_filter || 'allGames'
    
    // Set calculated stats
    if (existingSettings.calculated_stats) {
      calculatedStats.value = existingSettings.calculated_stats
    }
    
    console.log('=== Applied existing settings ===')
    console.log('Settings object:', settings.value)
    console.log('Seasons:', existingSettings.seasons)
    console.log('Leagues array:', existingSettings.leagues)
    console.log('Leagues count:', existingSettings.leagues?.length)
    console.log('Additional filter from DB:', existingSettings.additional_filter)
    console.log('Applied additional filter:', settings.value.additionalFilter)
  } else {
    // Use defaults based on season type
    settings.value.seasons = getDefaultSeasons()
    settings.value.leagues = [] // Empty array means all leagues
    settings.value.additionalFilter = 'allGames'
    console.log('Applied default settings:', settings.value)
  }
}

// Create mock games from saved stats
function createMockGamesFromSavedStats(existingSettings) {
  const mockGames = []
  const gameCount = existingSettings.calculated_stats?.gameCount || 10
  const stats = existingSettings.calculated_stats?.stats || existingSettings
  
  for (let i = 0; i < Math.min(gameCount, 10); i++) {
    mockGames.push({
      id: i + 1,
      game_date: new Date().toISOString().split('T')[0],
      season: existingSettings.seasons?.[0] || '2024-2025',
      league_name: 'All Leagues',
      team_name: 'Unknown Team',
      opponent_team_name: 'Opponent',
      minutes: parseFloat(existingSettings.per_game_minutes) || 30,
      points: parseFloat(stats.points || existingSettings.points) || 0,
      rebounds: parseFloat(stats.rebounds || existingSettings.total_rebounds) || 0,
      assists: parseFloat(stats.assists || existingSettings.assists) || 0,
      three_point_field_goal: parseFloat(stats['3pt'] || existingSettings.three_points_field_goal) || 0,
      steals: parseFloat(stats.steals || existingSettings.steals) || 0,
      blocks: parseFloat(stats.blocks || existingSettings.blocks) || 0,
      turnovers: parseFloat(stats.turnovers || existingSettings.turnovers) || 0,
      pra: parseFloat(stats.pra || 0) || 0,
      efficiency: 0
    })
  }
  
  return mockGames
}

// Save template
async function saveTemplate() {
  if (!calculatedStats.value) {
    console.log('No stats calculated yet')
    return
  }
  
  const totalMinutes = filteredGames.value.reduce((total, game) => {
    return total + (parseFloat(game.minutes) || 0)
  }, 0)
  
  const gameCount = filteredGames.value.length
  const perGameMinutes = gameCount > 0 ? totalMinutes / gameCount : 0
  
  const filterData = {
    player_id: props.playerId,
    season_template: props.season,
    leagues: settings.value.leagues,
    seasons: settings.value.seasons,
    additional_filter: settings.value.additionalFilter,
    filteredGames: filteredGames.value.length,
    totalMinutes: totalMinutes,
    perGameMinutes: perGameMinutes,
    calculatedStats: calculatedStats.value,
    totals: createTotalsObject(),
    per30: createPer30Object()
  }
  
  // Determine the correct API endpoint based on context
  const endpoint = props.context === 'minutes' 
    ? '/api/v2/projections-players/minute-settings/league-templates/save'
    : '/api/v2/projections-players/season-templates/save'
  
  try {
    const response = await axios.post(endpoint, filterData)
    console.log(`${props.context} template saved:`, response.data)
    
    // Show success popup
    showSavePopup.value = true
    
    // Auto-hide popup after 3 seconds
    setTimeout(() => {
      showSavePopup.value = false
    }, 3000)
    
  } catch (error) {
    console.error(`Error saving ${props.context} template:`, error)
    alert('Error saving template. Please try again.')
  }
}

function createTotalsObject() {
  const stats = calculatedStats.value.stats
  return {
    points: stats.points?.total || 0,
    rebounds: stats.rebounds?.total || 0,
    assists: stats.assists?.total || 0,
    three_pointers: stats['3pt']?.total || 0,
    pra: stats.pra?.total || 0,
    turnovers: stats.turnovers?.total || 0,
    blocks: stats.blocks?.total || 0,
    steals: stats.steals?.total || 0,
    minutes: filteredGames.value.reduce((total, game) => total + (parseFloat(game.minutes) || 0), 0)
  }
}

function createPer30Object() {
  const stats = calculatedStats.value.stats
  return {
    points: stats.points?.per30 || 0,
    rebounds: stats.rebounds?.per30 || 0,
    assists: stats.assists?.per30 || 0,
    three_pointers: stats['3pt']?.per30 || 0,
    pra: stats.pra?.per30 || 0,
    turnovers: stats.turnovers?.per30 || 0,
    blocks: stats.blocks?.per30 || 0,
    steals: stats.steals?.per30 || 0
  }
}

// Utility functions
function getSeasonLabel(season) {
  const labels = {
    'Last Season': 'Last Season',
    '2 Seasons ago': '2 Seasons Ago',
    '3 Seasons ago': '3 Seasons Ago',
    'League Avg': 'League Average',
    'Last 5 games': 'Last 5 Games',
    'Rest of Curr Season': 'Rest of Current Season'
  }
  return labels[season] || season
}

function getAdditionalFilterLabel(filterType) {
  const labels = {
    'lastFiveGames': 'Last 5 Games',
    'withoutLast5Games': 'Without Last 5',
    'allGames': 'All Games'
  }
  return labels[filterType] || 'All Games'
}

// Fetch games
async function fetchGames(leagues, seasons, additionalFilter) {
  // Without a player the backend loads every player's games and runs out of memory
  if (!props.playerId) return

  loading.value = true
  rows.value = []
  filteredGames.value = []
  
  try {
    const requestBody = {
      player_id: props.playerId,
      seasons: seasons.length > 0 ? seasons : [],
      leagues: leagues, // Empty array means all leagues
      additional_filter: additionalFilter
    }

    console.log('Fetching games with:', requestBody)
    console.log('Leagues array length:', leagues ? leagues.length : 'undefined')
    console.log('Leagues array content:', leagues)
    
    const response = await axios.post('/api/v2/games/player/filters', requestBody)
    let data = response.data || []
    
    // Apply additional filtering
    data = applyAdditionalFilter(data, additionalFilter)
    
    rows.value = [...data]
    filteredGames.value = [...data]
    
  } catch (error) {
    console.error('Error fetching games:', error)
    rows.value = []
    filteredGames.value = []
  } finally {
    loading.value = false
  }
}

function applyAdditionalFilter(games, filterType) {
  if (!games || games.length === 0) return games
  
  switch (filterType) {
    case 'lastFiveGames':
      const sortedGames = [...games].sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
      return sortedGames.slice(0, 5)
      
    case 'withoutLast5Games':
      const sortedGamesForRest = [...games].sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
      return sortedGamesForRest.slice(5)
      
    case 'allGames':
    default:
      return games
  }
}

// Event handlers
function closeModal() {
  emit('close')
}

function handleStatsUpdated(filteredStats) {
  console.log('Filtered stats updated:', filteredStats)
}

function handleFilteredRowsUpdated(filteredRowsData) {
  filteredGames.value = filteredRowsData
}

function handleStatsCalculated(stats) {
  calculatedStats.value = stats
  console.log('Stats calculated:', stats)
}

// Watchers
watch(
  () => [settings.value.leagues, settings.value.seasons, settings.value.additionalFilter],
  async ([leagues, seasons, additionalFilter]) => {
    console.log('Settings changed, fetching games:', { leagues, seasons, additionalFilter })
    await fetchGames(leagues, seasons, additionalFilter)
  },
  { deep: true }
)

// Watch for template data changes
watch(
  () => props.templateData,
  async (newTemplateData) => {
    if (newTemplateData) {
      console.log('Template data changed, reinitializing settings')
      console.log('New template data:', newTemplateData)
      await initializeSettings()
    }
  },
  { deep: true, immediate: true }
)

watch(rows, (newRows) => {
  if (newRows && newRows.length > 0) {
    filteredGames.value = [...newRows]
  } else {
    filteredGames.value = []
  }
}, { immediate: true })

// Lifecycle
onMounted(async () => {
  try {
    initializeSettings()
    const existingSettings = await getExistingSettings()
    
    if (existingSettings && existingSettings.calculated_stats) {
      // Use existing data
      console.log('Using existing calculated stats')
      // Create mock games from saved stats if they exist
      if (existingSettings.calculated_stats.gameCount > 0) {
        filteredGames.value = createMockGamesFromSavedStats(existingSettings)
      } else {
        // Fallback to fetching new data if no games in saved stats
        await fetchGames(
          settings.value.leagues, 
          settings.value.seasons, 
          settings.value.additionalFilter
        )
      }
    } else {
      // Fetch new data
      await fetchGames(
        settings.value.leagues, 
        settings.value.seasons, 
        settings.value.additionalFilter
      )
    }
  } catch (error) {
    console.error('Error initializing season data:', error)
    loading.value = false
  }
})
</script>

<template>
  <!-- Overlay Container -->
  <div v-if="show" class="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-8">
    <!-- Inner Container -->
    <div class="bg-white rounded-lg shadow-2xl w-full h-full flex flex-col overflow-hidden">
      <!-- Header -->
      <div class="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ getSeasonLabel(season) }} Template Configuration
          <span v-if="settings.seasons.length" class="text-sm text-gray-500 ml-2">
            ({{ settings.seasons.join(', ') }})
          </span>
          <span v-if="hasExistingSettings" class="text-sm text-green-600 ml-2 font-medium">
            <i class="fas fa-check-circle mr-1"></i>
            Using Saved Settings
          </span>
        </h1>
        
        <!-- Header Buttons -->
        <div class="flex items-center gap-3">
          <!-- Save Template Button -->
          <button
            @click="saveTemplate"
            class="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 font-semibold"
          >
            <i class="fas fa-save"></i>
            Save {{ getSeasonLabel(season) }} Template
          </button>
          
          <!-- Close Button -->
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600 focus:outline-none bg-white hover:bg-gray-50 rounded-lg p-3 transition-colors duration-200 border border-gray-200"
            @click="closeModal"
          >
            <span class="sr-only">Close</span>
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="flex flex-1 overflow-hidden">
        <!-- Sidebar -->
        <div class="w-80 bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto">
          <!-- Settings Status Info -->
          <div v-if="hasExistingSettings" class="p-4 bg-green-50 border-b border-green-200">
            <div class="flex items-center text-green-800">
              <i class="fas fa-info-circle mr-2"></i>
              <span class="text-sm font-medium">Using Saved Settings</span>
            </div>
            <div class="mt-2 text-xs text-green-700">
              <div>Seasons: {{ settings.seasons.join(', ') }}</div>
              <div>Leagues: {{ settings.leagues.length > 0 ? settings.leagues.join(', ') : 'All Leagues' }}</div>
              <div>Additional Filter: {{ getAdditionalFilterLabel(settings.additionalFilter) }}</div>
            </div>
          </div>
          
          <PlayerDetailsSidebar
            :seasons="seasons"
            :selected-season="settings.seasons"
            :selected-leagues="settings.leagues"
            :available-leagues="availableLeagues"
            :selected-additional-filter="settings.additionalFilter"
            @update:selectedSeason="val => settings.seasons = val"
            @update:selectedLeagues="val => settings.leagues = val"
            @update:additionalFilter="val => settings.additionalFilter = val"
          />
        </div>
        
        <!-- Main Content -->
        <div class="flex-1 flex flex-col bg-gray-50 overflow-y-auto">
          <!-- Stats Summary -->
          <div class="flex-shrink-0">
            <PlayerStatsSummary 
              :games="filteredGames" 
              :projected-minutes="30" 
              @stats-calculated="handleStatsCalculated"
            />
          </div>
          
          <!-- Table -->
          <div class="flex-1 overflow-y-auto p-6">
            <PlayerGameStatsTable 
              :key="`season-table-${props.playerId}-${settings.seasons.join('-')}-${settings.leagues.join('-')}`"
              :columns="columns" 
              :rows="rows" 
              @stats-updated="handleStatsUpdated" 
              @filtered-rows-updated="handleFilteredRowsUpdated" 
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Save Success Popup -->
    <div v-if="showSavePopup" 
         class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div class="bg-white rounded-lg shadow-2xl p-8 max-w-md mx-4">
        <div class="text-center">
          <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
            <i class="fas fa-check text-green-600 text-2xl"></i>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            Template Saved Successfully!
          </h3>
          <p class="text-sm text-gray-600 mb-4">
            Your {{ getSeasonLabel(season) }} template has been saved and is ready to use.
          </p>
          <button 
            @click="showSavePopup = false"
            class="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-colors">
            OK
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
